import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Plus, Edit, Trash2, Power, PowerOff, Mail, Save, X,
  UserCircle, FileSpreadsheet
} from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Senders = () => {
  const [senders, setSenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    sheet_column_value: '',
    is_active: true
  });

  const fetchSenders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/senders`, { withCredentials: true });
      setSenders(response.data);
    } catch (error) {
      console.error('Error fetching senders:', error);
      toast.error('Erreur lors du chargement des expéditeurs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSenders();
  }, [fetchSenders]);

  const resetForm = () => {
    setForm({
      first_name: '',
      last_name: '',
      email: '',
      sheet_column_value: '',
      is_active: true
    });
    setEditingItem(null);
  };

  const handleSave = async () => {
    if (!form.first_name || !form.last_name || !form.email || !form.sheet_column_value) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      if (editingItem) {
        await axios.put(`${API}/senders/${editingItem.id}`, form, { withCredentials: true });
        toast.success('Expéditeur mis à jour !');
      } else {
        await axios.post(`${API}/senders`, form, { withCredentials: true });
        toast.success('Expéditeur créé !');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchSenders();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la sauvegarde';
      toast.error(message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet expéditeur ?')) return;
    try {
      await axios.delete(`${API}/senders/${id}`, { withCredentials: true });
      toast.success('Expéditeur supprimé');
      fetchSenders();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggle = async (id) => {
    try {
      await axios.post(`${API}/senders/${id}/toggle`, {}, { withCredentials: true });
      fetchSenders();
    } catch (error) {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const openEdit = (sender) => {
    setEditingItem(sender);
    setForm({
      first_name: sender.first_name,
      last_name: sender.last_name,
      email: sender.email,
      sheet_column_value: sender.sheet_column_value,
      is_active: sender.is_active
    });
    setIsDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold font-heading tracking-tight text-foreground">
            Expéditeurs
          </h1>
          <p className="text-gray-600 mt-2 leading-relaxed text-sm sm:text-base">
            Gérez vos conseillers pour l'envoi personnalisé d'emails et SMS
          </p>
        </div>
        <Button
          onClick={() => { resetForm(); setIsDialogOpen(true); }}
          className="bg-primary text-white hover:bg-primary/90 rounded-full"
          data-testid="add-sender-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          Nouvel Expéditeur
        </Button>
      </div>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-800">Comment ça fonctionne ?</h4>
              <p className="text-sm text-blue-700 mt-1">
                Chaque expéditeur est associé à une valeur de colonne dans votre Google Sheet. 
                Lorsqu'une automation est déclenchée, le système identifie le conseiller 
                correspondant et envoie l'email depuis son adresse.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {senders.length === 0 ? (
          <Card className="border-dashed col-span-full">
            <CardContent className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500">Aucun expéditeur configuré</p>
              <p className="text-sm text-gray-400 mt-1">Ajoutez vos conseillers pour personnaliser les envois</p>
            </CardContent>
          </Card>
        ) : (
          senders.map((sender) => (
            <Card key={sender.id} className="hover:shadow-md transition-shadow" data-testid={`sender-${sender.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <UserCircle className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">
                        {sender.first_name} {sender.last_name}
                      </CardTitle>
                      <CardDescription className="text-sm">
                        {sender.email}
                      </CardDescription>
                    </div>
                  </div>
                  <Badge variant={sender.is_active ? "default" : "secondary"}>
                    {sender.is_active ? "Actif" : "Inactif"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <FileSpreadsheet className="h-4 w-4 text-gray-500" />
                    <span className="text-gray-600">Valeur Sheet:</span>
                    <span className="font-medium text-gray-900">{sender.sheet_column_value}</span>
                  </div>
                  
                  <div className="flex items-center justify-end gap-2 pt-2 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggle(sender.id)}
                      data-testid={`toggle-sender-${sender.id}`}
                    >
                      {sender.is_active ? <PowerOff size={16} /> : <Power size={16} />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(sender)}
                      data-testid={`edit-sender-${sender.id}`}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(sender.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-sender-${sender.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editingItem ? 'Modifier Expéditeur' : 'Nouvel Expéditeur'}
            </DialogTitle>
            <DialogDescription>
              Configurez les informations du conseiller pour l'envoi personnalisé
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">Prénom *</Label>
                <Input
                  id="first_name"
                  value={form.first_name}
                  onChange={(e) => setForm(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="Marie"
                  data-testid="sender-first-name"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Nom *</Label>
                <Input
                  id="last_name"
                  value={form.last_name}
                  onChange={(e) => setForm(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Dupont"
                  data-testid="sender-last-name"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email d'envoi *</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="marie.dupont@fidelis.com"
                data-testid="sender-email"
              />
              <p className="text-xs text-gray-500 mt-1">
                Cet email sera utilisé comme expéditeur pour Mailjet
              </p>
            </div>

            <div>
              <Label htmlFor="sheet_column_value">Valeur correspondante dans le Sheet *</Label>
              <Input
                id="sheet_column_value"
                value={form.sheet_column_value}
                onChange={(e) => setForm(prev => ({ ...prev, sheet_column_value: e.target.value }))}
                placeholder="Marie Dupont"
                data-testid="sender-sheet-value"
              />
              <p className="text-xs text-gray-500 mt-1">
                La valeur exacte qui apparaît dans la colonne "Conseiller" de votre Google Sheet
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="is_active"
                checked={form.is_active}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="is_active" className="cursor-pointer">Expéditeur actif</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} className="bg-primary text-white" data-testid="save-sender-btn">
              <Save size={16} className="mr-2" />
              {editingItem ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default Senders;
